/**
 * Network Monitor Service
 * Monitors browser online/offline state and provides network connectivity status
 */

import { CONNECTION_STATUS } from './constants';

class NetworkMonitorService {
  constructor() {
    this.isMonitoring = false;
    this.listeners = new Map();

    // Browser tracking: simple check to see if browser is toggled online/offline
    this.isBrowserOnline = navigator.onLine;

    // Connection tracking: endpoint calling to see if actual connection works
    // Use boolean for consistency (true = connected, false = disconnected)
    this.isInternetOn = true;
    this.pollingIntervalId = null;
    this.pollingIntervalMs = 1500;
    this.pollingEndpoint = null; // Set via config
    this.pollingTimeout = 2500;

    // Bind event handlers
    this.handleOnline = this.handleOnline.bind(this);
    this.handleOffline = this.handleOffline.bind(this);
    this.checkConnectivity = this.checkConnectivity.bind(this);

    // Set initial status: both browser + polling
    this.currentStatus = (this.isBrowserOnline && this.isInternetOn) ? CONNECTION_STATUS.CONNECTED : CONNECTION_STATUS.DISCONNECTED;
  }

  /**
   * Start monitoring with all strategies enabled
   * @param {Object} options - Configuration options
   * @param {string} options.pollingEndpoint - URL to poll for connectivity checks
   * @param {number} options.pollingInterval - Milliseconds between polls (default: 30000)
   */
  startMonitoring(options = {}) {
    if (this.isMonitoring) {
      console.warn('[NetworkMonitor] Already monitoring');
      return;
    }

    // Apply options
    if (options.pollingEndpoint) {
      this.pollingEndpoint = options.pollingEndpoint;
    }
    if (options.pollingInterval) {
      this.pollingIntervalMs = options.pollingInterval;
    }

    // Layer 1: Browser events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    
    // Layer 2: Active polling
    this.startPolling();
    
    this.isMonitoring = true;
    
    // Emit initial state
    this.emitStatusChange(this.currentStatus);
    
    console.log('[NetworkMonitor] Started monitoring browser + checking endpoint:', {
      pollingInterval: this.pollingIntervalMs,
      pollingEndpoint: this.pollingEndpoint,
    });
  }

  /**
   * Stop all monitoring strategies
   */
  stopMonitoring() {
    if (!this.isMonitoring) {
      return;
    }

    // Layer 1: Browser events
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    
    // Layer 2: Active polling
    this.stopPolling();
    
    this.isMonitoring = false;
    
    console.log('[NetworkMonitor] Stopped all monitoring');
  }

  /**
   * Handle browser online event
   */
  handleOnline() {
    console.log('[NetworkMonitor] Browser went ONLINE');
    this.isBrowserOnline = true;
    this.updateStatus();
  }

  /**
   * Handle browser offline event
   */
  handleOffline() {
    console.log('[NetworkMonitor] Browser went OFFLINE');
    this.isBrowserOnline = false;
    this.updateStatus();
  }

  /**
   * Update network status and emit changes
   */
  updateStatus() {
    const nextStatus = (this.isBrowserOnline && this.isInternetOn) ? CONNECTION_STATUS.CONNECTED : CONNECTION_STATUS.DISCONNECTED;

    if (this.currentStatus !== nextStatus) {
      console.log('[NetworkMonitor] Connectivity overall status change: ', nextStatus);
      this.currentStatus = nextStatus;
      this.emitStatusChange(nextStatus);
    }
  }

  /**
   * Emit status change to all listeners
   */
  emitStatusChange(status) {
    this.listeners.forEach((callback) => {
      try {
        callback(status);
      } catch (error) {
        console.error('[NetworkMonitor] Listener error:', error);
      }
    });
  }

  /**
   * Add listener for network status changes
   * @param {string} id - Unique identifier for the listener
   * @param {Function} callback - Callback function (receives status as parameter)
   */
  addListener(id, callback) {
    if (typeof callback !== 'function') {
      throw new Error('Listener callback must be a function');
    }
    
    this.listeners.set(id, callback);
    
    // Immediately call with current status
    callback(this.currentStatus);
  }

  /**
   * Remove listener
   * @param {string} id - Unique identifier for the listener
   */
  removeListener(id) {
    this.listeners.delete(id);
  }

  /**
   * Get current network status
   * @returns {string} Current connection status
   */
  getStatus() {
    return this.currentStatus;
  }

  /**
   * Check internet connectivity via HEAD request to static asset
   * @returns {Promise<boolean>} True if connected
   */
  async checkConnectivity() {
    if (!this.isBrowserOnline) {
      // skip checking early if browser is offline
      return false;
    }
    
    try {
      const endpoint = this.pollingEndpoint || '/manifest.json';
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.pollingTimeout);
      
      await fetch(endpoint, {
        method: 'HEAD',
        cache: 'no-cache',
        mode: 'no-cors',
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      // As long as we receive a response means the internet is working, if not, error below already catch
      if (!this.isInternetOn) {
        this.isInternetOn = true;
        console.log(`[NetworkMonitor] Polling status updated to CONNECT.`);
        this.updateStatus();
      }
      
    } catch (error) {
      // Network error, timeout, or abort = offline
      console.warn('[NetworkMonitor] Connectivity check failed:', error.message);
      console.log(`[NetworkMonitor] Polling status updated to DISCONNECT.`);
      this.isInternetOn = false;
      this.updateStatus();
      return false;
    }
  }

  /**
   * Start active polling for connectivity
   */
  startPolling() {
    if (this.pollingIntervalId) {
      return;
    }
    
    // Run first check immediately
    this.checkConnectivity();
    
    // Then poll at interval
    this.pollingIntervalId = setInterval(() => {
      this.checkConnectivity();
    }, this.pollingIntervalMs);
  }

  /**
   * Stop active polling
   */
  stopPolling() {
    if (this.pollingIntervalId) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
    }
  }

  /**
   * Cleanup all listeners and stop monitoring
   */
  cleanup() {
    this.stopMonitoring();
    this.listeners.clear();
  }
}

// Export singleton instance
export default new NetworkMonitorService();

// Export class for testing
export { NetworkMonitorService };
